import React, { useEffect, useRef, useState } from "react";
import { parseISO } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { ChevronLeft, ChevronRight, MapPin, Star, Ticket } from "lucide-react";

const TZ = "America/New_York";

// Gradient palette keyed by category — used as fallback background when
// a featured event doesn't have an image URL. Editorial-feeling, not
// pastel "AI slop".
const CAT_GRADIENT = {
  "Live Music":   "from-fuchsia-900 via-purple-800 to-indigo-900",
  Music:          "from-fuchsia-900 via-purple-800 to-indigo-900",
  Comedy:         "from-orange-800 via-rose-800 to-red-900",
  "Food & Drink": "from-amber-800 via-orange-800 to-red-800",
  Food:           "from-amber-800 via-orange-800 to-red-800",
  Sports:         "from-emerald-900 via-teal-800 to-cyan-900",
  Family:         "from-sky-800 via-blue-800 to-indigo-900",
  Arts:           "from-rose-800 via-pink-800 to-fuchsia-900",
  Culture:        "from-rose-800 via-pink-800 to-fuchsia-900",
  Community:      "from-slate-800 via-slate-700 to-stone-800",
  Nightlife:      "from-slate-950 via-purple-950 to-indigo-950",
  Outdoors:       "from-emerald-800 via-green-800 to-teal-900",
  Nature:         "from-emerald-800 via-green-800 to-teal-900",
  Default:        "from-[#1A3C5A] via-slate-800 to-slate-950",
};

const gradientFor = (cat) => CAT_GRADIENT[cat] || CAT_GRADIENT.Default;

function fmtWhen(iso) {
  try { return formatInTimeZone(parseISO(iso), TZ, "EEE MMM d · h:mm a"); }
  catch { return ""; }
}

/**
 * FeaturedCard — uniform size, image OR gradient. Renders identically
 * whether there are 1 or 20 in the strip.
 */
function FeaturedCard({ ev, size = "md" }) {
  const hasImage = Boolean(ev.image_url);
  const grad = gradientFor(ev.category);

  // Size variants: "lg" hero uses a taller aspect + bigger type.
  const isLg = size === "lg";
  const heightClass = isLg ? "h-72 md:h-[26rem]" : "aspect-[4/5]";
  const titleClass = isLg
    ? "text-2xl md:text-4xl font-display font-bold leading-tight"
    : "font-display font-bold text-white leading-snug text-base md:text-lg line-clamp-3";

  return (
    <a
      href={ev.source_url || "#"}
      target={ev.source_url ? "_blank" : undefined}
      rel="noreferrer"
      className={`group relative block w-full overflow-hidden rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5 ${heightClass}`}
      data-testid={`featured-card-${ev.id}`}
    >
      {hasImage ? (
        <>
          <img
            src={ev.image_url}
            alt={ev.title}
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/45 to-black/10" />
        </>
      ) : (
        <div className={`absolute inset-0 bg-gradient-to-br ${grad}`}>
          <div
            className="absolute inset-0 opacity-[0.15] mix-blend-overlay"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.6'/></svg>\")",
            }}
          />
        </div>
      )}

      {/* Featured star badge */}
      <div className="absolute top-3 left-3 flex items-center gap-1 rounded-full bg-amber-400/95 text-amber-950 px-2.5 py-1 text-[10px] font-display font-bold uppercase tracking-widest shadow-md">
        <Star size={11} strokeWidth={2.5} className="fill-amber-950" />
        Featured
      </div>

      {/* Category chip */}
      {ev.category && (
        <div className="absolute top-3 right-3 rounded-full bg-white/15 backdrop-blur-md text-white px-2.5 py-1 text-[10px] font-display font-semibold uppercase tracking-widest border border-white/20 max-w-[60%] truncate">
          {ev.category}
        </div>
      )}

      <div className="absolute inset-x-0 bottom-0 p-4 md:p-5 text-white">
        <div className="text-[10px] md:text-[11px] font-display font-semibold uppercase tracking-widest text-amber-300 mb-1.5">
          {fmtWhen(ev.start_datetime)}
        </div>
        <h3 className={titleClass}>
          {ev.title}
        </h3>
        <div className="mt-1.5 flex items-center gap-2 text-[12px] text-white/85">
          {ev.venue && (
            <span className="inline-flex items-center gap-1 min-w-0">
              <MapPin size={11} strokeWidth={2} className="shrink-0" />
              <span className="truncate">{ev.venue}{ev.town ? ` · ${ev.town}` : ""}</span>
            </span>
          )}
          {ev.is_paid && (
            <span className="inline-flex items-center gap-1 text-amber-300 shrink-0">
              <Ticket size={11} strokeWidth={2} /> Paid
            </span>
          )}
        </div>
      </div>
    </a>
  );
}


/**
 * Editorial layout — shape-shifts by count to look striking on the homepage.
 * 1 event   → single hero, full width
 * 2 events  → side-by-side on desktop
 * 3 events  → 1 large left + 2 stacked right
 * 4+ events → 1 large hero on top, remaining in a 3-col brick grid below
 */
function EditorialLayout({ events, contextLabel }) {
  const cap = events.slice(0, 7);
  const n = cap.length;

  const title = (
    <div className="flex items-baseline justify-between mb-4">
      <h2 className="font-display text-xl md:text-2xl font-bold tracking-tight text-[#1A3C5A]">
        <Star size={16} className="inline -mt-1 mr-1.5 fill-amber-400 text-amber-500" strokeWidth={2}/>
        Featured this week
        {contextLabel && (
          <span className="ml-2 text-sm md:text-base font-semibold text-slate-500">{contextLabel}</span>
        )}
      </h2>
      <span className="text-xs text-slate-500 uppercase tracking-widest font-display font-semibold">
        {events.length} pick{events.length === 1 ? "" : "s"}
      </span>
    </div>
  );

  if (n === 1) {
    return (
      <section className="mb-8" data-testid="featured-strip">
        {title}
        <FeaturedCard ev={cap[0]} size="lg" />
      </section>
    );
  }

  if (n === 2) {
    return (
      <section className="mb-8" data-testid="featured-strip">
        {title}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {cap.map(ev => <FeaturedCard key={ev.id} ev={ev} size="md" />)}
        </div>
      </section>
    );
  }

  if (n === 3) {
    return (
      <section className="mb-8" data-testid="featured-strip">
        {title}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <FeaturedCard ev={cap[0]} size="lg" />
          </div>
          <div className="grid grid-cols-1 gap-4">
            <FeaturedCard ev={cap[1]} size="md" />
            <FeaturedCard ev={cap[2]} size="md" />
          </div>
        </div>
      </section>
    );
  }

  // 4+ — hero on top, remainder in 3-col brick below.
  const [hero, ...rest] = cap;
  return (
    <section className="mb-8" data-testid="featured-strip">
      {title}
      <div className="flex flex-col gap-4">
        <FeaturedCard ev={hero} size="lg" />
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {rest.map(ev => (
            <FeaturedCard key={ev.id} ev={ev} size="md" />
          ))}
        </div>
      </div>
    </section>
  );
}


/**
 * FeaturedStrip — variant switch.
 *   variant="editorial" → shape-shifting hero+brick (great for homepage no-filter view)
 *   variant="carousel"  → uniform snap-scroll with arrows (consistent across filter states)
 * Auto-hides when `events` is empty.
 */
export default function FeaturedStrip({ events, contextLabel, variant = "carousel" }) {
  const scrollerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    if (variant !== "carousel") return;
    const el = scrollerRef.current;
    if (!el) return;
    // Define inside the effect so the listener registered and the listener
    // removed are the exact same function reference across effect re-runs.
    const update = () => {
      // Threshold of 24px accounts for the container's px-4 (16px) padding — at
      // rest the scroll origin sits at ~16px, not 0, so a simple `> 0` check
      // would mis-report Prev as enabled on initial load.
      setCanScrollLeft(el.scrollLeft > 24);
      setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [events, variant]);

  if (!events || events.length === 0) return null;

  if (variant === "editorial") {
    return <EditorialLayout events={events} contextLabel={contextLabel} />;
  }

  const scrollByOne = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const step = Math.max(el.clientWidth / 4, 240);
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  return (
    <section className="mb-8" data-testid="featured-strip">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="font-display text-xl md:text-2xl font-bold tracking-tight text-[#1A3C5A]">
          <Star size={16} className="inline -mt-1 mr-1.5 fill-amber-400 text-amber-500" strokeWidth={2}/>
          Featured
          {contextLabel && (
            <span className="ml-2 text-sm md:text-base font-semibold text-slate-500">
              {contextLabel}
            </span>
          )}
        </h2>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-500 uppercase tracking-widest font-display font-semibold">
            {events.length} pick{events.length === 1 ? "" : "s"}
          </span>
          {events.length > 4 && (
            <div className="hidden md:flex items-center gap-1.5" data-testid="featured-arrows">
              <button
                type="button"
                onClick={() => scrollByOne(-1)}
                disabled={!canScrollLeft}
                aria-label="Previous featured events"
                data-testid="featured-prev"
                className="h-8 w-8 grid place-items-center rounded-full border border-border bg-white text-[#1A3C5A] hover:border-[#1A3C5A] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={16} strokeWidth={2.25} />
              </button>
              <button
                type="button"
                onClick={() => scrollByOne(1)}
                disabled={!canScrollRight}
                aria-label="Next featured events"
                data-testid="featured-next"
                className="h-8 w-8 grid place-items-center rounded-full border border-border bg-white text-[#1A3C5A] hover:border-[#1A3C5A] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight size={16} strokeWidth={2.25} />
              </button>
            </div>
          )}
        </div>
      </div>

      <div
        ref={scrollerRef}
        className="flex gap-3 md:gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth -mx-4 px-4 pb-2 no-scrollbar"
        data-testid="featured-scroller"
      >
        {events.map((ev) => (
          <div
            key={ev.id}
            className="shrink-0 snap-start basis-[78%] sm:basis-[46%] md:basis-[31%] lg:basis-[calc(25%-0.75rem)]"
          >
            <FeaturedCard ev={ev} />
          </div>
        ))}
      </div>
    </section>
  );
}
